import {messages} from '@smartmixin/i18n';
import {Desktop, DeviceMobile} from '@phosphor-icons/react';
import {Box, Tab, Tabs} from '@mui/material';
import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import {useIntl} from 'react-intl';
import {HelpPlatform, useHelpPlatform} from '../PlatformContext';

type PlatformTabProps = {
  platform: HelpPlatform;
  children?: ReactNode;
};

// One platform's content inside a PlatformTabs group (from :::web/:::mobile)
export const PlatformTab = ({children}: PlatformTabProps) => (
  <Box>{children}</Box>
);

type PlatformTabsProps = {
  children?: ReactNode;
};

const PLATFORM_ICONS: Record<HelpPlatform, ReactElement> = {
  web: <Desktop size={18} />,
  mobile: <DeviceMobile size={18} />,
};

// Synced tab group: every group on the page follows the shared platform
// selection (HelpPlatformProvider), which persists across articles and is
// preset by the mobile app via ?platform=mobile.
const PlatformTabs = ({children}: PlatformTabsProps) => {
  const {formatMessage} = useIntl();
  const {platform, setPlatform} = useHelpPlatform();

  const tabs = Children.toArray(children).filter(
    (child): child is ReactElement<PlatformTabProps> =>
      isValidElement(child) &&
      Boolean((child.props as PlatformTabProps).platform),
  );
  if (tabs.length === 0) {
    return null;
  }

  const platforms = tabs.map((tab) => tab.props.platform);
  const active = platforms.includes(platform) ? platform : platforms[0];
  const activeTab = tabs.find((tab) => tab.props.platform === active);

  const labels: Record<HelpPlatform, string> = {
    web: formatMessage(messages.common.platformWebApp),
    mobile: formatMessage(messages.common.platformMobileApp),
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        overflow: 'hidden',
      }}>
      <Tabs
        value={active}
        onChange={(_event, value: HelpPlatform) => setPlatform(value)}
        sx={{
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontWeight: 500,
          },
        }}>
        {platforms.map((value) => (
          <Tab
            key={value}
            value={value}
            icon={PLATFORM_ICONS[value]}
            iconPosition="start"
            label={labels[value]}
          />
        ))}
      </Tabs>
      <Box sx={{px: 2, pt: 2, pb: 0.5}}>{activeTab}</Box>
    </Box>
  );
};

export default PlatformTabs;
